import { FormControl } from '@mui/material';
import { FC, useMemo } from 'react';
import { useQuery } from 'react-query';
import { alphaSort } from '../../helpers';
import { IDropdownResponse } from '../../models';
import { getContractAcceptanceStatuses } from '../../fetch/lookups/sales-portal';
import { AutoCompleteMultiple } from '../input/AutoCompleteMultiple';

interface ISalesStatusAutoComplete {
  value: string | number | string[] | number[];
  handleChange: (val: string | number | string[] | number[]) => void; // Accepts single or multiple values
  isDisabled?: boolean;
  setOptions?: (val: IDropdownResponse[]) => void;
  apiParams?: any;
  isRequired?: boolean;
  inputLabel?: string;
  isEnabled?: boolean;
  name?: string;
  handleClear?: () => void;
}

export const SalesStatusAutoComplete: FC<ISalesStatusAutoComplete> = ({
  inputLabel,
  isDisabled,
  isRequired,
  value,
  handleChange,
  setOptions,
  apiParams,
  name,
  isEnabled = true,
  handleClear
}) => {
  const { isLoading: isLoadingNeighborhoods, data: statusesData = [] } = useQuery<
    IDropdownResponse[],
    Error
  >(['contract-acceptance-filters'], () => getContractAcceptanceStatuses(), {
    notifyOnChangeProps: 'tracked',
    onSuccess: data => setOptions?.(data),
    enabled: isEnabled
  });

  const statuses = useMemo(() => {
    if (!Array.isArray(statusesData)) {
      return [];
    }
    return statusesData.map(r => ({
      ...r,
      fullName: `${r.description}`
    }));
  }, [statusesData]);

  const options = alphaSort(statuses, 'description').map(neighborhood => ({
    value: neighborhood?.value,
    label: neighborhood?.description
  }));

  const flattenedValue = useMemo(() => {
    if (Array.isArray(value)) {
      return value.flat();
    }
    return [value];
  }, [value]);

  const selectedOptions = useMemo(() => {
    if (Array.isArray(flattenedValue)) {
      return options.filter(option =>
        (flattenedValue as (string | number)[]).includes(option.value)
      );
    }
    const selectedOption = options.find(option => option.value === flattenedValue);
    return selectedOption ? [selectedOption] : [];
  }, [flattenedValue, options]);

  return (
    <FormControl fullWidth variant="outlined" size="small" required={isRequired}>
      <AutoCompleteMultiple
        name={name || null}
        options={options}
        label={isLoadingNeighborhoods ? 'Loading...' : inputLabel ?? 'Statuses'}
        value={selectedOptions}
        required={isRequired}
        disableCloseOnSelect={true}
        onChange={(event, newValue) => {
          if (!newValue || (Array.isArray(newValue) && newValue.length === 0)) {
            handleClear && handleClear();
            handleChange([]);
          } else if (Array.isArray(newValue)) {
            handleChange(newValue.map(option => option.value));
          }
        }}
      />
    </FormControl>
  );
};
